import { Typography } from '@mui/material';
import { Skeleton } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useFeathers } from '../../app/util';
import styled from 'styled-components';
import { Check, Edit, Error, OpenInNew, Star } from '@mui/icons-material';
import moment from 'moment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useHistory } from 'react-router-dom';
import NoteIcon from '@mui/icons-material/Note';
import { Done, DoneAll } from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import LinearProgress from '@mui/material/LinearProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { ModalInner } from '../lower-order';
import { useSelector } from 'react-redux';
import { TemporalReportMini, onDateToReport } from '../TemporalReportMini';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { resolveAutoScouter } from '../../screens/AutoScouterDetail/resolveAutoScouter';
import { buildCountyFilterQuery } from './county-filter-query';

const ON_BALLOT_LOOKBACK_DAYS = 30;

const buildCumulativeAnalyticsReport = (
  statusesForDistrict = [],
  {
    analyticsKey,
    reportKey,
    title,
    units,
    valueLabel
  }
) => {
  const statusReports = statusesForDistrict
    .map(status => {
      const report = onDateToReport({
        onDate: status?.analytics?.[analyticsKey] || {},
        key: `implementation-district-${reportKey}-${status?.contentType || 'unknown'}`,
        title,
        units,
        valueLabel,
        lookbackDays: ON_BALLOT_LOOKBACK_DAYS,
        cumulative: true
      });

      return {
        contentType: status?.contentType,
        report,
        total: report?.result?.total ?? 0
      };
    })
    .filter(({ report }) => Boolean(report));

  if(statusReports.length === 0) return null;

  const selectedReport = statusReports.reduce((maxReport, report) => (
    report.total > maxReport.total ? report : maxReport
  ));

  return selectedReport.report;
};

const SupportedDistrictList = ({
  statusFilter,
  typeFilter,
  eauTypeFilter,
  countyFilter,
  searchTerm,
  pageNumber,
  onChangePageNumber,
  electionKey,
  onChangeCount,
  style,
  className,
}) => {
  const feathers = useFeathers();
  const history = useHistory();
  const [ implementationDistricts, setImplementationDistricts ] = useState(null);
  const [districtsFull, setDistrictsFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [selectedDistricts, setSelectedDistricts] = useState(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionType, setCompletionType] = useState('all'); // 'all', 'races', 'measures'
  const user = useSelector(state => state.user);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [openingDistrictId, setOpeningDistrictId] = useState(null);
  const [autoScouterError, setAutoScouterError] = useState(null);
  const numPerPage = 50;  // the number of districts to show per page

  const districtStatusesById = React.useMemo(() => {
    return (implementationDistricts || []).reduce((acc, v) => {
      if(acc[v.district]) {
        acc[v.district].push(v);
      } else {
        acc[v.district] = [v];
      }
      return acc;
    }, {});
  }, [implementationDistricts]);


  const loadDistricts = async () => {
    setLoading(true)
  

    /* our first query comes from the implementation-districts service */
    const districtType = typeFilter === 'all' ? null : typeFilter;

    const isEauDistrictFilter =
      eauTypeFilter === 'eau' ? true
      : eauTypeFilter === 'non-eau' ? false
      : undefined;

    // note – this is of the assumption that measure implementation districts are a subset of 
    // race implementation districts
    const queryCommon = {
      election: electionKey,
      active: true,
      ...(districtType ? { districtType } : {}),
      ...(isEauDistrictFilter !== undefined ? { isEauDistrict: isEauDistrictFilter } : {}),
      ...buildCountyFilterQuery(countyFilter),
      ...(searchTerm ? { districtLongName: { $search: searchTerm } } : {})
    }
    const queryRaces = {
      ...queryCommon,
      contentType: 'races',
      ...(['complete', 'incomplete'].includes(statusFilter)
        ? { $districtStatus: statusFilter }
        : {}),
    };

    const implementationDistrictsRaces = await feathers.getService('implementation-districts').find({
      query: {
        ...queryRaces,
        $limit: numPerPage,
        $skip: numPerPage * (pageNumber - 1),
        $includeInternalFields: true,
        $includeAnalytics: true,
        $sort: { expectedReadership: -1, districtLongName: 1 }
      },
    });
    if(onChangeCount) onChangeCount(implementationDistrictsRaces.total);
    setCount(implementationDistrictsRaces.total);

    const implementationDistrictsMeasures = await feathers.getService('implementation-districts').find({
      query: {
        ...queryCommon,
        contentType: 'measures',
        district: implementationDistrictsRaces.data.map(d => d.district),
        $limit: numPerPage,
        $includeInternalFields: true,
        $includeAnalytics: true,
        $sort: { expectedReadership: -1, districtLongName: 1 }
      },
    });

    const implementationDistrictsAll = [].concat(implementationDistrictsRaces.data, implementationDistrictsMeasures.data);

    setImplementationDistricts(implementationDistrictsAll);
    const orderOfID = implementationDistrictsAll.map(d => d.district);

    const filteredDistricts = [...new Set(implementationDistrictsAll.map(d => d.district))]
    const numDistricts = filteredDistricts.length;
    const chunkSize = 20;
    const numQueries = Math.ceil(numDistricts / chunkSize);
    const districtChunks = Array.from({ length: numQueries }, (_, i) => {
      return filteredDistricts.slice(i * chunkSize, (i + 1) * chunkSize);
    });

    const districtData = await Promise.all(
      districtChunks.map(async (chunk) => {
        const query = { $limit: chunkSize, _id: chunk };
        const districts = await feathers
          .getService('districts')
          .find({ query });
        return districts.data;
      })
    );

    const districtDataSorted = districtData.flat().sort((a, b) => {
      return orderOfID.indexOf(a._id) - orderOfID.indexOf(b._id);
    });

    setDistrictsFull(districtDataSorted);

    setLoading(false);
  };

  const loadImplementationDistrictsForModal = async () => {
    // If all districts on the current page are selected, we assume user wants to select all districts
    const isSelectingAll = districtsFull && selectedDistricts.size === districtsFull.length;
    
    // Base query parameters
    const queryCommon = {
      election: electionKey,
      active: true,
      ...(typeFilter !== 'all' ? { districtType: typeFilter } : {}),
      ...(eauTypeFilter === 'eau' ? { isEauDistrict: true } : {}),
      ...(eauTypeFilter === 'non-eau' ? { isEauDistrict: false } : {}),
      ...buildCountyFilterQuery(countyFilter),
      ...(searchTerm ? { districtLongName: { $search: searchTerm } } : {}),
    };

    const chunkSize = 100;
    const loadAll = async (query) => {
      const countResult = await feathers.getService('implementation-districts').find({
        query: { ...query, $limit: 0 },
      });
      const chunks = [];
      const numQueries = Math.ceil(countResult.total / chunkSize);
      for (let i = 0; i < numQueries; i++) {
        const result = await feathers.getService('implementation-districts').find({
          query: {
            ...query,
            $limit: chunkSize,
            $skip: i * chunkSize,
          },
        });
        chunks.push(...result.data);
      }
      return chunks;
    };

    const racesDistricts = await loadAll({
      ...queryCommon,
      contentType: 'races',
      ...(['complete', 'incomplete'].includes(statusFilter)
        ? { $districtStatus: statusFilter }
        : {}),
      ...(isSelectingAll ? {} : { district: Array.from(selectedDistricts) }),
    });
    const districtIds = racesDistricts.map(district => district.district);
    const measuresDistricts = districtIds.length
      ? await loadAll({
          ...queryCommon,
          contentType: 'measures',
          district: districtIds,
        })
      : [];

    const allDistricts = [...racesDistricts, ...measuresDistricts];
    
    // Count complete vs incomplete
    const completeCount = allDistricts.filter(d => d.status === 'complete').length;
    const incompleteCount = allDistricts.length - completeCount;

    setModalData({
      total: allDistricts.length,
      complete: completeCount,
      incomplete: incompleteCount,
      districts: allDistricts,
      races: racesDistricts,
      measures: measuresDistricts
    });
  };

  const handleSubmit = async () => {
    if (!isConfirmed || !modalData) return;
    
    setIsSubmitting(true);
    
    try {
      // Get current user ID from feathers
      const userId = user._id;

      // Filter districts based on completion type
      let districtsToProcess = [];

      if (completionType === 'all') {
        districtsToProcess = modalData.districts.filter(district =>
          district.status === 'incomplete'
        );
      } else if (completionType === 'races') {
        districtsToProcess = modalData.races.filter(district =>
          district.status === 'incomplete'
        );
      } else if (completionType === 'measures') {
        districtsToProcess = modalData.measures.filter(district =>
          district.status === 'incomplete'
        );
      }
      const chunkSize = 5;

      const districtChunks = Array.from(
        { length: Math.ceil(districtsToProcess.length / chunkSize) },
        (_, i) => districtsToProcess.slice(i * chunkSize, (i + 1) * chunkSize)
      );

      setProgress({ current: 0, total: districtChunks.length });

      // Completing an implementation district cancels its research task in the service after-hook.
      const dataToPatch = {
        source: 'no source provided',
        scoutedWithDistricts: [],
        isPrimaryDistrict: true,
        status: 'complete',
        checkedAt: new Date(),
        notes: `No content found for this district, bulk checked off (${completionType} only)`,
        user: userId
      };

      for (let i = 0; i < districtChunks.length; i++) {
        const chunk = districtChunks[i];
        await Promise.all(
          (chunk || []).map(district =>
            feathers.getService('implementation-districts').patch(district._id, dataToPatch)
          )
        );
        setProgress(prev => ({ ...prev, current: i + 1 }));
      }

      setIsModalOpen(false);
      loadDistricts(); // Refresh the main list
    } catch (error) {
      console.error('Error marking districts as complete:', error);
      // TODO: Handle error appropriately
    } finally {
      setIsSubmitting(false);
      setIsConfirmed(false);
      setProgress({ current: 0, total: 0 });

      // reload districts and remove the checked off districts
      loadDistricts();
      setSelectedDistricts(new Set());
    }
  };

  const handleOpenModal = async () => {
    setIsModalOpen(true);
    setModalData(null);
    setIsConfirmed(false);
    setCompletionType('all'); // Reset to default
    await loadImplementationDistrictsForModal();
  };

  useEffect(() => {
    if (feathers) loadDistricts();
  }, [feathers, statusFilter, typeFilter, eauTypeFilter, countyFilter, pageNumber, searchTerm]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = new Set((districtsFull || []).map(district => district._id));
      setSelectedDistricts(newSelected);
    } else {
      setSelectedDistricts(new Set());
    }
  };

  const handleSelectDistrict = (districtId) => {
    const newSelected = new Set(selectedDistricts);
    if (selectedDistricts.has(districtId)) {
      newSelected.delete(districtId);
    } else {
      newSelected.add(districtId);
    }
    setSelectedDistricts(newSelected);
  };

  const handleOpenAutoScouter = async (districtId) => {
    if (!feathers || openingDistrictId) return;

    const autoScouterWindow = window.open('about:blank', '_blank');
    if (autoScouterWindow) autoScouterWindow.opener = null;

    setOpeningDistrictId(districtId);
    setAutoScouterError(null);
    try {
      const autoScouter = await resolveAutoScouter(feathers, electionKey, districtId);
      const autoScouterPath = `/elections/${electionKey}/auto-scouters/${autoScouter._id}`;
      if (autoScouterWindow) {
        autoScouterWindow.location.href = autoScouterPath;
      } else {
        history.push(autoScouterPath);
      }
    } catch (error) {
      autoScouterWindow?.close();
      console.error('Unable to open auto-scouter:', error);
      setAutoScouterError(error?.message || 'Unable to open auto-scouter.');
    } finally {
      setOpeningDistrictId(null);
    }
  };

  return (
    <Wrapper style={style} className={className}>
      <Typography
        variant='body2'
        style={{ gridColumn: '1 / 2', gridRow: `1 / 2`, padding: '0' }}
      >
        <Checkbox
          indeterminate={selectedDistricts.size > 0 && selectedDistricts.size < (districtsFull || []).length}
          checked={districtsFull && selectedDistricts.size === districtsFull.length}
          onChange={handleSelectAll}
          disabled={loading}
          size='small'
        />
      </Typography>
      <Typography
        variant='body2'
        style={{ gridColumn: '2 / 3', gridRow: `1 / 2`, fontWeight: 'bold', marginBottom: '4px' }}
      >
        District
      </Typography>
      <Typography
        variant='body2'
        style={{ gridColumn: '3 / 4', gridRow: `1 / 2`, fontWeight: 'bold', marginBottom: '4px' }}
      >
        Status
      </Typography>
      <Typography
        variant='body2'
        style={{ gridColumn: '4 / 5', gridRow: `1 / 2`, fontWeight: 'bold', marginBottom: '4px' }}
      >
        Analytics
      </Typography>
      <div style={{ gridColumn: '5 / 6', gridRow: `1 / 2` }}></div>

      {selectedDistricts.size > 0 && (
        <>
          <Separator
            style={{ gridColumn: '1 / -1', gridRow: '2 / 3' }}
          />
          <Cell
            style={{ 
              gridColumn: '1 / -1', 
              gridRow: '2 / 3',
              flexDirection: 'row',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '8px 0'
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Check/>}
              onClick={handleOpenModal}
            >
              Mark as complete
            </Button>
            <Typography variant="body2" style={{ opacity: 0.7 }}>
              {districtsFull && selectedDistricts.size === districtsFull.length 
                ? `All districts selected (${count} total)`
                : `${selectedDistricts.size} district${selectedDistricts.size !== 1 ? 's' : ''} selected`
              }
            </Typography>
          </Cell>
        </>
      )}

      {loading &&
        [1, 2, 3].map((i) => {
          const rowOffset = selectedDistricts.size > 0 ? 1 : 0;
          const rowNum = i + 1 + rowOffset;
          return (
            <React.Fragment key={i}>
              <Separator
                style={{ gridColumn: '1 / -1', gridRow: `${rowNum} / ${rowNum + 1}` }}
              />
              <Cell
                style={{ gridColumn: '1 / 2', gridRow: `${rowNum} / ${rowNum + 1}` }}
              >
                <Skeleton variant='text' width={30} />
              </Cell>
              <Cell
                style={{ gridColumn: '2 / 3', gridRow: `${rowNum} / ${rowNum + 1}` }}
              >
                <Skeleton variant='text' width={150} />
              </Cell>
              <Cell
                style={{ gridColumn: '3 / 4', gridRow: `${rowNum} / ${rowNum + 1}` }}
              >
                <Skeleton variant='text' width={150} />
              </Cell>
            </React.Fragment>
          );
        })}
      {!loading &&
        (districtsFull || []).map((district, idx) => {
          const rowOffset = selectedDistricts.size > 0 ? 1 : 0;
          const i = idx + 1 + rowOffset;
          const statusesForDistrict = districtStatusesById[district._id];
          const isMeasuresDistrict = Boolean(statusesForDistrict?.find(v => v.contentType === 'measures'));

          const raceStatusForDistrict = statusesForDistrict?.find(v => v.contentType === 'races');
          const measureStatusForDistrict = statusesForDistrict?.find(v => v.contentType === 'measures');

          const measuresComplete = measureStatusForDistrict?.status === 'complete';
          const expectedReadership = raceStatusForDistrict?.expectedReadership ?? 0;
          const isPriority = (statusesForDistrict || []).some(s => s.isPriority);
          const numViews = (statusesForDistrict || []).reduce((acc, v) => Math.max(acc, v?.analytics?.views ?? 0), 0);
          const numUsers = (statusesForDistrict || []).reduce((acc, v) => Math.max(acc, v?.analytics?.onBallot ?? 0), 0);
          const onBallotReport = buildCumulativeAnalyticsReport(statusesForDistrict || [], {
            analyticsKey: 'onBallotByDate',
            reportKey: 'on-ballot',
            title: 'Users within the district',
            units: 'users',
            valueLabel: 'Users'
          });
          const viewsReport = buildCumulativeAnalyticsReport(statusesForDistrict || [], {
            analyticsKey: 'viewsByDate',
            reportKey: 'views',
            title: 'Content views for this district',
            units: 'views',
            valueLabel: 'Views'
          });

          let icon = null,
            detailMessage = null,
            statusWritten = null;
          if (!raceStatusForDistrict) {
            // error icon
            icon = <Error style={{ color: 'red' }} />;
            statusWritten = 'Error';
          } else if (raceStatusForDistrict?.status === 'complete' && (measuresComplete || !isMeasuresDistrict)) {
            icon = <DoneAll style={{ color: '#4CAF50' }} />;
            const racesCheckedBy =
              raceStatusForDistrict?.automatedService || raceStatusForDistrict.user?.name || raceStatusForDistrict.user?.firstName;
            const checkedAt = moment(raceStatusForDistrict.checkedAt).fromNow();

            detailMessage = `Races checked by ${racesCheckedBy || 'unknown'} ${checkedAt ? ` ${checkedAt}` : ''}`
            if(isMeasuresDistrict) {
              const measuresCheckedBy =
                measureStatusForDistrict?.automatedService || measureStatusForDistrict.user?.name || measureStatusForDistrict.user?.firstName;
              const measuresCheckedAt = moment(measureStatusForDistrict.checkedAt).fromNow();
              detailMessage += `, Referendums checked by ${measuresCheckedBy || 'unknown'} ${measuresCheckedAt ? ` ${measuresCheckedAt}` : ''}`
            }

            statusWritten = 'Complete (races and referendums)';
          } else if (raceStatusForDistrict?.status === 'complete' && isMeasuresDistrict && !measuresComplete) {
            icon = <Done style={{ color: '#4CAF50' }} />;
            const checkedBy =
              raceStatusForDistrict?.automatedService || raceStatusForDistrict.user?.name || raceStatusForDistrict.user?.firstName;
            const checkedAt = moment(raceStatusForDistrict.checkedAt).fromNow();
            detailMessage = `Checked by ${checkedBy || 'unknown'} ${checkedAt ? ` ${checkedAt}` : ''}`
            statusWritten = 'Races complete, referendums incomplete';
          } else {
            // an incomplete icon
            icon = <div />;
            statusWritten = 'Incomplete';
            if(measuresComplete) {
              detailMessage = `No races scouted (referendums complete)`
            } else {
              detailMessage = isMeasuresDistrict ? `No referendums or races scouted` : `No races scouted`
            }
          }

          const notesForDistrict = (statusesForDistrict || []).map(v => v.notes).flat();
          const sourcesForDistrict = [...new Set((statusesForDistrict || []).map(v => v.source).filter(v => v))];

          return (
            <React.Fragment key={i}>
              <Separator
                style={{ gridColumn: '1 / -1', gridRow: `${i + 1} / ${i + 2}` }}
              />
              <Cell
                style={{ gridColumn: '1 / 2', gridRow: `${i + 1} / ${i + 2}`, padding: '0' }}
              >
                <Checkbox
                  checked={selectedDistricts.has(district._id)}
                  onChange={() => handleSelectDistrict(district._id)}
                  size='small'
                />
              </Cell>
              <Cell
                style={{ gridColumn: '2 / 3', gridRow: `${i + 1} / ${i + 2}` }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px'}}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DistrictNameButton
                      type='button'
                      onClick={() => handleOpenAutoScouter(district._id)}
                      disabled={Boolean(openingDistrictId)}
                    >
                      <Typography variant='body1' component='span'>
                        <b>{district.longName}</b>
                      </Typography>
                    </DistrictNameButton>
                    {isPriority && (
                      <Star style={{ height: '16px', width: '16px', color: '#ffa726' }} />
                    )}
                  </div>
                  {
                    expectedReadership
                    ? <Typography variant='body2' style={{ opacity: 0.8, fontSize: '14px' }}>Expected readers: {(expectedReadership ?? 0).toLocaleString()}</Typography>
                    : <Typography variant='body2' style={{ opacity: 0.8, fontSize: '14px' }}>Population: {(district.population ?? 0).toLocaleString()}</Typography>
                  }
                </div>
              </Cell>
              <Cell
                style={{
                  gridColumn: '3 / 4',
                  gridRow: `${i + 1} / ${i + 2}`,
                  gap: '4px',
                }}
              >
                <div style={{ display: 'flex', gap: '4px' }}>
                  {icon}
                  <Typography variant='body1'>{statusWritten}</Typography>
                </div>
                {(detailMessage) && (
                  <Typography
                    variant='caption'
                    style={{ color: 'rgba(0, 0, 0, 0.54)' }}
                  >
                    {detailMessage}
                  </Typography>
                )}
              </Cell>
              <Cell
                style={{
                  gridColumn: '4 / 5',
                  gridRow: `${i + 1} / ${i + 2}`,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '8px',
                }}
              >
                <Tooltip title='Number of users within the district'>
                  <AnalyticsCell>
                    {onBallotReport ? (
                      <TemporalReportMini
                        report={onBallotReport}
                        summaryMetric='total'
                        metricSlotWidth={56}
                      />
                    ) : (
                      <Typography variant='body1'>{`${numUsers ?? 0}`.toLocaleString()} users</Typography>
                    )}
                  </AnalyticsCell>
                </Tooltip>
                <Tooltip title='Number of content views for this district'>
                  <AnalyticsCell>
                    {viewsReport ? (
                      <TemporalReportMini
                        report={viewsReport}
                        summaryMetric='total'
                        metricSlotWidth={56}
                        color='#876ce9'
                        metricColor='#22034f'
                      />
                    ) : (
                      <Typography variant='body1'>{`${numViews ?? 0}`.toLocaleString()} views</Typography>
                    )}
                  </AnalyticsCell>
                </Tooltip>
              </Cell>
              <Cell
                style={{
                  gridColumn: '5 / 6',
                  gridRow: `${i + 1} / ${i + 2}`,
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {
                  (notesForDistrict?.length > 0) && 
                  <Tooltip title='View notes'>
                    <IconButton
                      size='small'
                      onClick={(e) => { console.log('view notes', notesForDistrict) }}
                    >
                      <NoteIcon />
                    </IconButton>
                  </Tooltip>
                }
                {
                  (sourcesForDistrict || []).map((source, idx) => (
                    <Tooltip title='View source' key={idx} >
                      <a href={source}  key={idx} target='_blank' rel='noreferrer'>
                        <IconButton
                          size='small'
                        >
                          <OpenInNew style={{ }}/>
                        </IconButton>
                      </a>
                    </Tooltip>
                  ))
                }
                <Tooltip title='Open auto-scouter'>
                  <span>
                    <IconButton
                      size='small'
                      onClick={() => handleOpenAutoScouter(district._id)}
                      disabled={Boolean(openingDistrictId)}
                    >
                      {openingDistrictId === district._id
                        ? <CircularProgress size={20} />
                        : <Edit />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Cell>
            </React.Fragment>
          );
        })}
        {
          !loading &&
          <div style={{ gridColumn: '1 / -1', gridRow: `${(districtsFull || []).length + 2 + (selectedDistricts.size > 0 ? 1 : 0)} / ${(districtsFull || []).length + 3 + (selectedDistricts.size > 0 ? 1 : 0)}`, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
            <Pagination
              count={Math.ceil(count / numPerPage)}
              page={pageNumber}
              onChange={(e, newPage) => onChangePageNumber(newPage)}
              color="primary"
            />
          </div>
        }

      <Modal
        open={isModalOpen}
        onClose={() => !isSubmitting && setIsModalOpen(false)}
      >
        <ModalInner>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            {completionType === 'all' ? 'Mark Districts as Complete' : 
             completionType === 'races' ? 'Mark Race Districts as Complete' : 
             'Mark Measure Districts as Complete'}
          </Typography>
          {!modalData ? (
            <Typography>Loading...</Typography>
          ) : (
            <>
              <Typography variant="body1" style={{ marginBottom: '8px' }}>
                {modalData.total} implementation district{modalData.total !== 1 ? 's' : ''} will be affected. ({modalData.races?.length} races, {modalData.measures?.length} measures)
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '24px' }}>
                Of these, {modalData.complete} are already complete.
              </Typography>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <FormControl component="fieldset" style={{ marginBottom: '16px' }}>
                  <FormLabel component="legend">Completion Type</FormLabel>
                  <RadioGroup
                    value={completionType}
                    onChange={(e) => setCompletionType(e.target.value)}
                    disabled={isSubmitting}
                  >
                    <FormControlLabel 
                      value="all" 
                      control={<Radio />} 
                      label={`Mark all districts as complete (${modalData.total} total)`} 
                    />
                    <FormControlLabel 
                      value="races" 
                      control={<Radio />} 
                      label={`Mark only race districts as complete (${modalData.races?.length || 0} districts)`} 
                    />
                    <FormControlLabel 
                      value="measures" 
                      control={<Radio />} 
                      label={`Mark only measure districts as complete (${modalData.measures?.length || 0} districts)`} 
                    />
                  </RadioGroup>
                </FormControl>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <Typography>
                    I confirm these {completionType === 'all' ? 'districts have no ballot content' :
                                   completionType === 'races' ? 'districts have no race content' :
                                   'districts have no measure content'}
                  </Typography>
                </div>
                
                {isSubmitting && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(progress.current / progress.total) * 100} 
                    />
                    <Typography variant="body2" color="textSecondary" align="center">
                      Processing chunk {progress.current} of {progress.total}
                    </Typography>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <Button
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!isConfirmed || isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 
                     completionType === 'all' ? 'Mark All as Complete' :
                     completionType === 'races' ? 'Mark Races as Complete' :
                     'Mark Measures as Complete'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </ModalInner>
      </Modal>
      <Snackbar
        open={Boolean(autoScouterError)}
        autoHideDuration={6000}
        onClose={() => setAutoScouterError(null)}
      >
        <Alert severity='error' onClose={() => setAutoScouterError(null)}>
          {autoScouterError}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
};



const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 1fr 0.5fr auto;
  align-items: center;
`;

const Separator = styled.div`
  width: calc(100% + 24px * 2);
  margin-left: -24px;
  height: 1px;
  background-color: #e0e0e0;
  align-self: start;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
`;

const DistrictNameButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    cursor: wait;
  }
`;

const AnalyticsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`

export { SupportedDistrictList };
